import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, mergeMap, of, tap } from 'rxjs';

import { AuthService } from 'src/app/services/auth.service';
import { SalesService } from 'src/app/services/sales.service';
import { HttpErrorHandlerService } from '../../../services/http-error-handler.service';
import { SpinnerService } from '../../services/spinner.service';
import { ToastService } from '../../services/toast.service';
import { LookupService } from '../../../services/lookup.service';
import * as SaleActions from './sales.action';

@Injectable()
export class SalesEffect {
  constructor(
    private actions$: Actions,

    private saleService: SalesService,
    private authService: AuthService,
    private store: Store,
    private toastService: ToastService,
    private lookupService: LookupService,
    private spinnerService: SpinnerService,
    private httpErrorHandlerService: HttpErrorHandlerService
  ) {}

  loadAllSales$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SaleActions.GetSales),
      mergeMap(() => {
        return this.saleService.getSales().pipe(
          map((sales) => {
            return SaleActions.GetSalesSuccess({ sales });
          }),
          catchError((err) => {
            return of(SaleActions.GetSalesFailure({ err }));
          })
        );
      })
    )
  );

  loadSaleById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SaleActions.GetSaleById),
      mergeMap((model) => {
        return this.saleService.getSale(model.saleId).pipe(
          map((sale) => {
            return SaleActions.GetSaleByIdSuccess({ sale });
          }),
          catchError((err) => {
            return of(SaleActions.GetSaleByIdFailure({ err }));
          })
        );
      })
    )
  );

  loadSaleHorses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SaleActions.GetSaleHorses),
      mergeMap((model) => {
        return this.saleService.getSaleHorses(model.saleId).pipe(
          map((saleHorses) => {
            return SaleActions.GetSaleHorsesSuccess({ saleHorses });
          }),
          catchError((err) => {
            return of(SaleActions.GetSaleHorsesFailure(err));
          })
        );
      })
    )
  );
  loadSaleStatuses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SaleActions.GetSaleStatuses),
      tap(() => this.spinnerService.addRequest('loadSaleStatuses')),
      mergeMap((model) => {
        return this.lookupService.getSaleStatuses(model.saleId).pipe(
          map((response) => {
            this.spinnerService.popRequest('loadSaleStatuses');
            return SaleActions.GetSaleStatusesSuccess({ saleStatuses: response });
          }),
          catchError((err) => {
            this.spinnerService.popRequest('loadSaleStatuses');
            return of(SaleActions.GetSaleStatusesFailure({ err: err }));
          })
        );
      })
    )
  );

  loadSaleHorse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SaleActions.GetSaleHorse),
      mergeMap((model) => {
        return this.saleService.getSaleHorse(model.saleId, model.horseId).pipe(
          map((saleHorse) => {
            return SaleActions.GetSaleHorseSuccess({ saleHorse });
          }),
          catchError((err) => {
            return of(SaleActions.GetSaleHorseFailure({ err }));
          })
        );
      })
    )
  );

  loadSaleTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SaleActions.GetSaleTemplate),
      mergeMap((model) => {
        return this.saleService.getSaleTemplate(model.saleId).pipe(
          map((saleTemplate) => {
            return SaleActions.GetSaleTemplateSuccess({ saleTemplate });
          }),
          catchError((err) => {
            return of(SaleActions.GetSaleTemplateFailure(err));
          })
        );
      })
    )
  );

  syncSaleHorse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SaleActions.SyncTwSaleHorseData),
      mergeMap((model) => {
        return this.authService.refreshTwSaleHorseData(model.saleId).pipe(
          map((response) => {
            this.toastService.success("Data refreshed successfully", "Success!");
            return SaleActions.SyncTwSaleHorseDataSuccess({ response });
          }),
          catchError((err) => {
            this.httpErrorHandlerService.ShowErrorMessage(err);
            return of(SaleActions.SyncTwSaleHorseDataFailure(err));
          })
        );
      })
    )
  );

  updateSaleHorseStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SaleActions.updateSaleHorseStatus),
      mergeMap((model) => {
        return this.saleService.updateSaleHorseStatus(model.saleHorse).pipe(
          map((response) => {
            return SaleActions.updateSaleHorseStatusSuccess({ response });
          }),
          catchError((err) => {
            this.httpErrorHandlerService.ShowErrorMessage(err);
            return of(SaleActions.updateSaleHorseStatusFailure(err));
          })
        );
      })
    )
  );

  updateSaleMemberAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SaleActions.UpdateSaleMemberAccess),
      mergeMap((model) => {
        return this.saleService.updateSaleMemberAccess(model.model).pipe(
          map((response) => {
            this.toastService.success("Success!", "Permissions are updated successfully!");
            return SaleActions.UpdateSaleMemberAccessSuccess({ response });
          }),
          catchError((err) => {
            this.httpErrorHandlerService.ShowErrorMessage(err);
            return of(SaleActions.UpdateSaleMemberAccessFailure(err));
          })
        );
      })
    )
  );

  getSaleMemberAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SaleActions.GetSaleMemberAccess),
      mergeMap((model) => {
        return this.saleService.getSaleMemberAccess(model.memberId).pipe(
          map((response) => {
            return SaleActions.GetSaleMemberAccessSuccess({ response });
          }),
          catchError((err) => {
            this.httpErrorHandlerService.ShowErrorMessage(err);
            return of(SaleActions.GetSaleMemberAccessFailure(err));
          })
        );
      })
    )
  );


  loadMemberAccessForSale$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SaleActions.GetMemberAccessForSale),
      mergeMap((model) => {
        return this.authService
          .getSaleMemberAccessDataAsObservable(model.saleId, model.horseId)
          .pipe(
            mergeMap((memberAccess) => {
              if (memberAccess) {
                return of(
                  SaleActions.GetMemberAccessForSaleSuccess({ memberAccess })
                );
              } else {
                return this.authService.getMemberAccessData(model.saleId).pipe(
                  map((memberAccess) => {
                    return SaleActions.GetMemberAccessForSaleSuccess({
                      memberAccess,
                    });
                  }),
                  catchError((err) => {
                    return of(SaleActions.GetMemberAccessForSaleFailure(err));
                  })
                );
              }
            })
          );
      })
    )
  );

  acceptTermsAndConditions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SaleActions.AcceptSaleTermsAndConditions),
      mergeMap((model) => {
        return this.saleService.acceptSaleTermsAndConditions(model.saleId).pipe(
          map((isAccepted) => {
            return SaleActions.AcceptSaleTermsAndConditionsSuccess({
              saleId: model.saleId,
              isAccepted,
            });
          }),
          catchError((err) => {
            return of(SaleActions.AcceptSaleTermsAndConditionsFailure(err));
          })
        );
      })
    )
  );
}
